import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import ConfirmationStep from '../../shared/ConfirmationStep';
import { StyMultiStepModal } from '../../shared/StyMultiStepModal';
import { BudgetProposalWithResourcesT } from 'routes/BudgetModule/config/types';
import { useTheme } from '@mui/material';
import { useDeleteCustomCadrage } from '../hook';
import { trackEventDeleteCustomCadrage } from './helper';
import { UseMutateFunction } from '@tanstack/react-query';

type Props = {
    isModalShown: boolean;
    setShowModal: DispatchState<boolean>;
    budgetProposal: BudgetProposalWithResourcesT;
    updateCadrageByChapter?: UseMutateFunction;
};

export default function DeleteCustomCadrageModal({
    isModalShown,
    setShowModal,
    budgetProposal,
    updateCadrageByChapter = undefined,
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const theme = useTheme();
    const [confirmationIsValid, setConfirmationIsValid] =
        useState<boolean>(false);

    const { mutate: deleteCustomCadrage } =
        useDeleteCustomCadrage(budgetProposal);

    const onConfirmAction = () => {
        deleteCustomCadrage();
        trackEventDeleteCustomCadrage(budgetProposal.name);
        if (updateCadrageByChapter) {
            updateCadrageByChapter();
        }
    };

    const steps = [
        {
            confirmAction: {
                enabled: confirmationIsValid,
                onClick: onConfirmAction,
                label: 'common.confirm',
            },
            stepBackAction: {
                onClick: () => {
                    setShowModal(false);
                },
                enabled: true,
            },
            disableClose: true,
            node: (
                <ConfirmationStep
                    title={t(
                        updateCadrageByChapter
                            ? 'adminBudgetSpace.cadrage.deleteCustomCadrageFromDetails.title'
                            : 'adminBudgetSpace.cadrage.deleteCustomCadrage.title',
                    )}
                    text={
                        <Trans
                            i18nKey={
                                updateCadrageByChapter
                                    ? 'adminBudgetSpace.cadrage.deleteCustomCadrageFromDetails.text'
                                    : 'adminBudgetSpace.cadrage.deleteCustomCadrage.text'
                            }
                            values={{ budgetProposalName: budgetProposal.name }}
                            components={{
                                bold: (
                                    <strong
                                        style={{
                                            color: theme.palette.primary.main,
                                        }}
                                    />
                                ),
                            }}
                        />
                    }
                    confirmationIsValid={confirmationIsValid}
                    setConfirmationIsValid={setConfirmationIsValid}
                />
            ),
        },
    ];
    return (
        <StyMultiStepModal
            isOpen={isModalShown}
            onClose={() => {
                setShowModal(false);
            }}
            maxWidth="md"
            steps={steps}
            showCloseIcon={true}
        />
    );
}
